import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import {
  CampaignType,
  EntityIdType,
  SbCreative,
  StrategyStateEnum,
  UpdateTacosStrategyGroupRequest,
} from "@front/m19-api-client";
import { StrategyEx } from "@front/m19-models";
import { AccountSelectionService, SbStrategyService, TacosStrategiesService, TacosStrategy } from "@front/m19-services";
import {
  IButtonDirective,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ILabelComponent,
  ITab,
  ITabsComponent,
  ModalRef,
  ModalService,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActivityComponent, ActivityFilter } from "@m19-board/activities/activity/activity.component";
import { StateBadgeComponent } from "@m19-board/shared/state-badge/state-badge.component";
import { StrategyInfoBlocComponent } from "@m19-board/shared/strategy-info-bloc/strategy-info-bloc.component";
import { combineLatest, map, of, switchMap, tap } from "rxjs";
import { TacosAdTypesComponent } from "./tacos-ad-types/tacos-ad-types.component";
import { TacosProductsComponent } from "./tacos-products/tacos-products.component";
import { TacosStatsComponent } from "./tacos-stats/tacos-stats.component";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { FormsModule } from "@angular/forms";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";

enum Section {
  PRODUCTS,
  CAMPAIGNS,
  STATS,
  ACTIVITIES,
}

@Component({
  selector: "app-tacos-strategy-page",
  imports: [
    CommonModule,
    StrategyInfoBlocComponent,
    IButtonDirective,
    SpinnerComponent,
    RouterModule,
    ITabsComponent,
    StateBadgeComponent,
    ActivityComponent,
    TacosStatsComponent,
    TacosProductsComponent,
    TacosAdTypesComponent,
    TranslocoDirective,
    EditableTextComponent,
    NavBreadcrumbDirective,
    IFormFieldComponent,
    ILabelComponent,
    IInputDirective,
    FormsModule,
    IInputGroupDirective,
    IInputGroupInput,
    IInputGroupSuffixDirective,
    IIconComponent,
  ],
  templateUrl: "./tacos-strategy-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TacosStrategyPageComponent {
  readonly Section = Section;
  readonly CampaignType = CampaignType;
  private readonly modalService = inject(ModalService);
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly toastrService = inject(ToasterService);
  private readonly sbStrategyService = inject(SbStrategyService);

  readonly destroyRef = inject(DestroyRef);

  readonly am = toSignal(this.accountSelection.singleAccountMarketplaceSelection$);
  readonly isReadOnly = toSignal(this.accountSelection.readOnlyMode$);

  activityFilter = computed<ActivityFilter[]>(() => {
    const creativeIds = this.sbCreatives()?.map((c) => c.creativeId);

    return [
      { primaryType: EntityIdType.tacosStrategyGroupId, primaryId: this.tacosStrategyEx()?.tacosStrategyGroupId },
      ...(creativeIds?.map((c) => ({ primaryType: EntityIdType.creativeId, primaryId: c })) ?? []),
    ];
  });

  private readonly tacosStrategy$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    takeUntilDestroyed(this.destroyRef),
    switchMap((am) =>
      combineLatest([
        this.route.paramMap,
        this.tacosStrategiesService.getTacosStrategyIndex(am.accountId, am.marketplace),
      ]),
    ),
    map(([params, tacosStrategyIndex]) => tacosStrategyIndex.get(Number(params.get("tacosId")))),
    tap((s) => {
      if (!s) {
        this.router.navigate(["/advertising/tacos-strategies"]);
      }
    }),
  );

  readonly tacosStrategyEx = toSignal<TacosStrategy>(
    this.tacosStrategy$.pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap((tacosStrategy) =>
        this.tacosStrategiesService.getTacosStrategies(
          tacosStrategy!.accountId!,
          tacosStrategy!.marketplace!,
          tacosStrategy!.tacosStrategyGroupId!,
        ),
      ),
    ),
  );

  readonly spStrategy = computed<SpStrategyEx | undefined>(() => this.tacosStrategyEx()?.spStrategy);

  readonly sbStrategy = computed<StrategyEx | undefined>(() => {
    const sbStrategy = this.tacosStrategyEx()?.sbStrategy;
    if (!sbStrategy) return undefined;
    return sbStrategy;
  });

  readonly sbCreatives = computed<SbCreative[] | undefined>(() => this.tacosStrategyEx()?.sbCreatives);

  readonly sbAsins = toSignal(
    combineLatest([this.accountSelection.singleAccountMarketplaceSelection$, toObservable(this.sbStrategy)]).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(([am, sbStrategy]) => {
        if (sbStrategy) {
          return this.sbStrategyService.getSbAsinsAsArray(am.accountId, am.marketplace, sbStrategy.strategyId!);
        }
        return of([]);
      }),
    ),
  );

  sbAsinsString = computed<string[] | undefined>(() => this.sbAsins()?.map((a) => a.asin));

  readonly missingAsinsInSb = computed<string[] | undefined>(() => {
    if (!this.sbStrategy()) return undefined;
    const spAsins: string[] | undefined = this.spStrategy()?.asins?.map((a) => a.asin);
    return spAsins?.filter((a) => !this.sbAsinsString()?.includes(a));
  });

  loading = signal(false);
  modalLoading = signal(false);
  stateUpdateLoading = signal(false);

  tacosTargetInput = signal<number | undefined>(undefined);
  strategyNameInput = signal<string | undefined>(undefined);

  @ViewChild("targetModal") targetModal!: TemplateRef<any>;
  @ViewChild("editNameModal") editNameModal!: TemplateRef<any>;

  modalRef: ModalRef<void> | undefined;

  tabs = signal<ITab<Section>[]>([
    {
      label: this.translocoService.translate("common.products"),
      value: Section.PRODUCTS,
      icon: "package",
    },
    {
      label: this.translocoService.translate("tacos-strategy.ad_types"),
      value: Section.CAMPAIGNS,
      icon: "campaign",
    },
    { label: "Stats", value: Section.STATS, icon: "chartLine" },
    {
      label: this.translocoService.translate("page-title.activities"),
      value: Section.ACTIVITIES,
      icon: "clock",
    },
  ]);
  activeTab = signal<Section>(Section.PRODUCTS);
  selectedSection = computed(() => this.tabs()[this.activeTab()].value);

  hideModal() {
    this.modalRef?.close();
  }

  openTargetModal() {
    this.modalRef = this.modalService.openModal(this.targetModal, {
      modalTitle: this.translocoService.translate("algo-mode-selection.tacos_target"),
    });
  }

  updateTacosTarget() {
    this.modalLoading.set(true);
    if (this.tacosStrategyEx()) {
      if (this.tacosTargetInput()! <= 0 || this.tacosTargetInput()! > 100) {
        this.toastrService.error(
          this.translocoService.translate("tacos-strategy-page.tacos_target_must_be_between_1_and_100"),
        );
        this.modalLoading.set(false);
        return;
      }

      const updateParams: UpdateTacosStrategyGroupRequest = {
        accountId: this.am()!.accountId,
        marketplace: this.am()!.marketplace,
        tacosTarget: this.tacosTargetInput()! / 100,
        tacosStrategyGroupId: this.tacosStrategyEx()!.tacosStrategyGroupId!,
      };

      this.tacosStrategiesService.updateTacosStrategyGroup(updateParams).subscribe({
        next: () => {
          this.modalLoading.set(false);
          this.toastrService.success(this.translocoService.translate("tacos-strategy-page.tacos_target_updated"));
          this.hideModal();
          this.tacosTargetInput.set(undefined);
        },
        error: (err) => {
          this.modalLoading.set(false);
          this.toastrService.error(err);
        },
      });
    }
  }

  updateStrategyName() {
    if (!this.strategyNameInput()) return;
    if (this.strategyNameInput() && this.spStrategy()) {
      this.modalLoading.set(true);
      this.tacosStrategiesService
        .updateTacosStrategyName({
          accountId: this.am()!.accountId,
          marketplace: this.am()!.marketplace,
          tacosStrategyGroupId: this.tacosStrategyEx()!.tacosStrategyGroupId!,
          name: this.strategyNameInput()!,
        })
        .subscribe({
          next: () => {
            this.toastrService.success(this.translocoService.translate("sp-strategy-group-page.strategy_name_updated"));
            this.modalLoading.set(false);
            this.hideModal();
            this.strategyNameInput.set(undefined);
          },
          error: (err) => {
            this.toastrService.error(err);
            this.modalLoading.set(false);
            this.strategyNameInput.set(undefined);
          },
        });
    }
  }

  updateStrategyGroupState(state: StrategyStateEnum) {
    this.stateUpdateLoading.set(true);
    this.tacosStrategiesService
      .updateTacosStrategyGroup({
        accountId: this.am()!.accountId,
        marketplace: this.am()!.marketplace,
        tacosStrategyGroupId: this.tacosStrategyEx()!.tacosStrategyGroupId!,
        state,
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("tacos-strategy-page.strategy_group_state_updated"),
          );
          this.stateUpdateLoading.set(false);
        },
        error: (err) => {
          this.toastrService.error(err);
          this.stateUpdateLoading.set(false);
        },
      });
  }
}
